import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

export const SESSION_WARNING_MILLISECONDS = 2 * 60 * 1000;

export const millisecondsUntil = (expiresAt, now = Date.now()) => {
  const expiration = Date.parse(expiresAt || '');
  return Number.isFinite(expiration) ? Math.max(0, expiration - now) : 0;
};

export const formatSessionCountdown = milliseconds => {
  const totalSeconds = Math.max(0, Math.ceil(Number(milliseconds || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const SessionExpiryDialog = ({
  expiresAt,
  canExtend = true,
  clockOffsetMilliseconds = 0,
  onExtend,
  onLogout,
  onExpired,
}) => {
  const clockOffset = Number.isFinite(Number(clockOffsetMilliseconds))
    ? Number(clockOffsetMilliseconds)
    : 0;
  const [remaining, setRemaining] = useState(() => millisecondsUntil(expiresAt, Date.now() + clockOffset));
  const [extending, setExtending] = useState(false);
  const [extensionError, setExtensionError] = useState('');
  const expirationHandled = useRef(false);

  useEffect(() => {
    expirationHandled.current = false;
    setExtensionError('');

    const update = () => {
      const nextRemaining = millisecondsUntil(expiresAt, Date.now() + clockOffset);
      setRemaining(nextRemaining);
      if (nextRemaining <= 0 && !expirationHandled.current) {
        expirationHandled.current = true;
        onExpired();
      }
      return nextRemaining;
    };

    const initialRemaining = update();
    if (initialRemaining <= 0) return undefined;

    // Sleep until the warning window when possible. Once visible, update the
    // accessible countdown every second. Recalculating from the server expiry
    // prevents background-tab timer throttling from extending the session.
    const firstDelay = initialRemaining > SESSION_WARNING_MILLISECONDS
      ? initialRemaining - SESSION_WARNING_MILLISECONDS
      : Math.min(1000, initialRemaining);
    let intervalId;
    const timeoutId = window.setTimeout(() => {
      update();
      intervalId = window.setInterval(update, 1000);
    }, Math.max(1, firstDelay));

    const refreshAfterVisibilityChange = () => {
      if (document.visibilityState === 'visible') update();
    };
    document.addEventListener('visibilitychange', refreshAfterVisibilityChange);
    window.addEventListener('focus', update);
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId) window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshAfterVisibilityChange);
      window.removeEventListener('focus', update);
    };
  }, [clockOffset, expiresAt, onExpired]);

  const open = remaining > 0 && remaining <= SESSION_WARNING_MILLISECONDS;
  const countdown = useMemo(() => formatSessionCountdown(remaining), [remaining]);
  const progress = Math.min(100, Math.max(0, (remaining / SESSION_WARNING_MILLISECONDS) * 100));

  const extendSession = async () => {
    setExtending(true);
    setExtensionError('');
    try {
      await onExtend();
    } catch (error) {
      setExtensionError(error?.message || 'The session could not be extended. Please try again.');
    } finally {
      setExtending(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      disableEscapeKeyDown
      aria-labelledby="session-expiry-title"
    >
      <DialogTitle id="session-expiry-title">Your session is about to expire</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning">
            For your security, you will be automatically logged out in{' '}
            <strong aria-live="polite">{countdown}</strong>.
          </Alert>
          <LinearProgress
            variant="determinate"
            value={progress}
            color="warning"
            aria-label="Time remaining in session"
          />
          <Typography variant="body2" color="text.secondary">
            {canExtend
              ? 'Select Extend Session to keep working. Unsaved changes will be lost if the session expires.'
              : 'This session has reached its maximum duration. Save your work and log in again.'}
          </Typography>
          {extensionError && <Alert severity="error">{extensionError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onLogout} disabled={extending} color="inherit">Log Out</Button>
        {canExtend && (
          <Button onClick={extendSession} disabled={extending} variant="contained" autoFocus>
            {extending ? 'Extending...' : 'Extend Session'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiryDialog;
